const HIDDEN_CHARS = [
  { pattern: /\u200B/g, label: 'ZWS', name: 'Zero-width space' },
  { pattern: /\u200C/g, label: 'ZWNJ', name: 'Zero-width non-joiner' },
  { pattern: /\u200D/g, label: 'ZWJ', name: 'Zero-width joiner' },
  { pattern: /\uFEFF/g, label: 'BOM', name: 'Byte order mark' },
  { pattern: /\u00AD/g, label: 'SHY', name: 'Soft hyphen' },
  { pattern: /\u200E/g, label: 'LRM', name: 'Left-to-right mark' },
  { pattern: /\u200F/g, label: 'RLM', name: 'Right-to-left mark' },
  { pattern: /\u2028/g, label: 'LS', name: 'Line separator' },
  { pattern: /\u2029/g, label: 'PS', name: 'Paragraph separator' },
  { pattern: /\u2060/g, label: 'WJ', name: 'Word joiner' },
  { pattern: /\u00A0/g, label: 'NBSP', name: 'Non-breaking space' },
];

// &nbsp; HTML entity (Quill stores non-breaking spaces this way)
const NBSP_ENTITY_PATTERN = /&nbsp;/gi;

// Control chars \u0000-\u001F except \t(\u0009), \n(\u000A), \r(\u000D)
const CONTROL_CHAR_PATTERN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

const MARKER_STYLE = 'background:#fca5a5;color:#991b1b;border-radius:3px;padding:0 2px;font-size:0.7em;font-family:monospace;vertical-align:middle;';

/**
 * Detect hidden/invisible Unicode characters in HTML content.
 * Checks both raw Unicode chars and &nbsp; HTML entities.
 * Returns { found, count, types } where types lists the human-readable names.
 */
export function detectHiddenChars(html) {
  if (!html) return { found: false, count: 0, types: [] };

  let totalCount = 0;
  const types = [];

  // Check &nbsp; entities in the raw HTML (before stripping tags)
  const nbspEntityMatches = html.match(NBSP_ENTITY_PATTERN);
  if (nbspEntityMatches) {
    totalCount += nbspEntityMatches.length;
    types.push('Non-breaking space');
  }

  // Strip HTML tags to check text content for Unicode hidden chars
  const text = html.replace(/<[^>]*>/g, '');

  for (const { pattern, name } of HIDDEN_CHARS) {
    // Skip NBSP Unicode check if we already found &nbsp; entities
    if (name === 'Non-breaking space' && types.includes('Non-breaking space')) {
      const matches = text.match(pattern);
      if (matches) totalCount += matches.length;
      continue;
    }
    const matches = text.match(pattern);
    if (matches) {
      totalCount += matches.length;
      types.push(name);
    }
  }

  const controlMatches = text.match(CONTROL_CHAR_PATTERN);
  if (controlMatches) {
    totalCount += controlMatches.length;
    types.push('Control character');
  }

  return { found: totalCount > 0, count: totalCount, types };
}

/**
 * Strip hidden/invisible Unicode characters from HTML content.
 * Replaces &nbsp; entities and \u00A0 with regular spaces; removes all others.
 * Preserves valid HTML tags and formatting.
 */
export function sanitizeHiddenChars(html) {
  if (!html) return html;

  let cleaned = html;

  // Replace &nbsp; HTML entities with regular spaces
  cleaned = cleaned.replace(NBSP_ENTITY_PATTERN, ' ');

  for (const { pattern, label } of HIDDEN_CHARS) {
    if (label === 'NBSP') {
      cleaned = cleaned.replace(pattern, ' ');
    } else {
      cleaned = cleaned.replace(pattern, '');
    }
  }
  cleaned = cleaned.replace(CONTROL_CHAR_PATTERN, '');

  return cleaned;
}

/**
 * Return a version of the HTML where each hidden character is replaced
 * with a visible colored marker so the admin can see exactly where they are.
 */
export function highlightHiddenChars(html) {
  if (!html) return html || '';

  let result = html;

  // Highlight &nbsp; HTML entities
  result = result.replace(
    NBSP_ENTITY_PATTERN,
    `<span style="${MARKER_STYLE}" title="Non-breaking space (&amp;nbsp;)">[NBSP]</span> `,
  );

  for (const { pattern, label } of HIDDEN_CHARS) {
    result = result.replace(
      pattern,
      `<span style="${MARKER_STYLE}" title="${label}">[${label}]</span>`,
    );
  }
  result = result.replace(
    CONTROL_CHAR_PATTERN,
    `<span style="${MARKER_STYLE}" title="Control char">[CTRL]</span>`,
  );

  return result;
}
